import React, { useEffect, useRef, useState } from 'react';
import { Clock, Maximize, Minimize, Pause, Play, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatSeconds, trainingMediaUrl } from '@/components/agent-handbook/trainingMedia';

// ─── localStorage progress fallback ──────────────────────────────────────────
// Catches the case where the API save was throttled / failed before the user
// refreshed. Read it back on mount and compare with the server value; take
// whichever is further ahead so we never lose progress.

const STORAGE_KEY = (id: number) => `training_progress_${id}`;
const RESUME_REWIND_SECONDS = 8;
const RESUME_REWIND_THRESHOLD = 10;

type LocalProgress = { currentTime: number; duration: number; savedAt: number };

const readLocalProgress = (id: number): LocalProgress | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY(id));
        if (!raw) return null;
        const parsed = JSON.parse(raw) || {};
        return {
            currentTime: Number(parsed.currentTime) || 0,
            duration: Number(parsed.duration) || 0,
            savedAt: Number(parsed.savedAt) || 0,
        };
    } catch {
        return null;
    }
};

const writeLocalProgress = (id: number, currentTime: number, duration: number) => {
    try {
        localStorage.setItem(STORAGE_KEY(id), JSON.stringify({ currentTime, duration, savedAt: Date.now() }));
    } catch {
        /* storage disabled / quota — fine, server is the source of truth */
    }
};

const clearLocalProgress = (id: number) => {
    try { localStorage.removeItem(STORAGE_KEY(id)); } catch { /* ignore */ }
};

const computeResumeTime = (raw: number) => {
    if (raw <= 0) return 0;
    if (raw < RESUME_REWIND_THRESHOLD) return raw;
    return Math.max(0, raw - RESUME_REWIND_SECONDS);
};

export type TrainingItemForPlayer = {
    id: number;
    videoTitle?: string | null;
    videoUrl?: string | null;
    videoObjectKey?: string | null;
    progress?: {
        maxWatchedSeconds?: number;
        lastPositionSeconds?: number;
        durationSeconds?: number;
        watchedAt?: string | null;
        completedAt?: string | null;
    } | null;
};

type Props = {
    item: TrainingItemForPlayer;
    onProgress: (payload: Record<string, any>, silent?: boolean) => Promise<boolean>;
};

export const TrainingVideoPlayer = ({ item, onProgress }: Props) => {
    const playerRef = useRef<HTMLDivElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const maxWatchedRef = useRef(Number(item.progress?.maxWatchedSeconds || 0));
    const lastSavedRef = useRef(0);
    const lastSaveFailedRef = useRef(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [currentTime, setCurrentTime] = useState(Number(item.progress?.lastPositionSeconds || 0));
    const [duration, setDuration] = useState(Number(item.progress?.durationSeconds || 0));
    const [maxWatched, setMaxWatched] = useState(Number(item.progress?.maxWatchedSeconds || 0));
    const [speed, setSpeed] = useState('1');
    const [loadError, setLoadError] = useState<string | null>(null);
    const speeds = ['1', '1.25', '1.5', '2'];
    const resolvedSrc = trainingMediaUrl(item.videoObjectKey || item.videoUrl);

    useEffect(() => {
        setLoadError(null);
        setCurrentTime(0);
        setIsPlaying(false);
        lastSavedRef.current = 0;
    }, [resolvedSrc]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        video.playbackRate = Number(speed);
    }, [speed]);

    useEffect(() => {
        const onFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === playerRef.current);
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, []);

    const saveProgress = async (time: number, total: number, ended = false, silent = true, force = false) => {
        const roundedTime = Math.floor(time);
        const roundedDuration = Math.floor(total || duration);
        // 3s heartbeat — but if the previous save failed, retry on the very next
        // tick by bypassing the throttle, regardless of the elapsed seconds.
        if (!force && !ended && !lastSaveFailedRef.current && roundedTime - lastSavedRef.current < 3) return;

        // Always write to localStorage first; if the API call fails, this stays
        // as the recovery point on the next mount.
        writeLocalProgress(item.id, roundedTime, roundedDuration);

        lastSavedRef.current = roundedTime;
        try {
            const ok = await onProgress({ action: 'progress', currentSeconds: roundedTime, durationSeconds: roundedDuration, ended }, silent);
            lastSaveFailedRef.current = ok === false;
        } catch {
            // Network/API failure — don't throw. The next heartbeat retries,
            // and localStorage keeps the latest position safe.
            lastSaveFailedRef.current = true;
        }
    };

    // Whenever a fresh progress payload comes in (e.g. after the server responds
    // to a save), reconcile in-memory refs/states with the server AND the local
    // fallback. Whichever value is further ahead wins.
    useEffect(() => {
        const serverMax = Number(item.progress?.maxWatchedSeconds || 0);
        const local = readLocalProgress(item.id);
        const effectiveMax = Math.max(serverMax, local?.currentTime || 0);
        if (effectiveMax > maxWatchedRef.current) {
            maxWatchedRef.current = effectiveMax;
            setMaxWatched(effectiveMax);
        }
        const serverDuration = Number(item.progress?.durationSeconds || 0);
        if (serverDuration && !duration) setDuration(serverDuration);
    }, [item.id, item.progress?.maxWatchedSeconds, item.progress?.durationSeconds]);

    // Drop the localStorage cache once the server marks this training complete.
    useEffect(() => {
        if (item.progress?.completedAt) clearLocalProgress(item.id);
    }, [item.id, item.progress?.completedAt]);

    // Best-effort flush on pause, tab hide, or unload so the agent doesn't
    // lose the last few seconds when refreshing.
    useEffect(() => {
        const flush = () => {
            const video = videoRef.current;
            if (!video) return;
            if (video.currentTime <= 0) return;
            void saveProgress(video.currentTime, video.duration || duration, false, true, true);
        };
        const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', flush);
        window.addEventListener('beforeunload', flush);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', flush);
            window.removeEventListener('beforeunload', flush);
        };
    }, [duration]);

    const onLoadedMetadata = () => {
        const video = videoRef.current;
        if (!video) return;
        const total = Math.floor(video.duration || 0);
        setDuration(total);

        const serverPos = Number(item.progress?.lastPositionSeconds || 0);
        const local = readLocalProgress(item.id);
        const localPos = local?.currentTime || 0;
        const rawResume = Math.max(serverPos, localPos);

        // If localStorage is further than the server's max-watched record,
        // raise the cap before seeking so onSeeking doesn't snap us back.
        if (rawResume > maxWatchedRef.current) {
            maxWatchedRef.current = rawResume;
            setMaxWatched(rawResume);
        }

        const resumeAt = computeResumeTime(rawResume);
        if (resumeAt > 0) video.currentTime = resumeAt;
    };

    const onSeeking = () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.currentTime > maxWatchedRef.current + 1.5) {
            video.currentTime = maxWatchedRef.current;
        }
        // Capture the new position immediately so a refresh right after the
        // jump still resumes there.
        void saveProgress(video.currentTime, video.duration || duration, false, true, true);
    };

    const onTimeUpdate = () => {
        const video = videoRef.current;
        if (!video) return;
        const time = video.currentTime;
        const total = video.duration || duration;
        setCurrentTime(time);
        if (time > maxWatchedRef.current) {
            maxWatchedRef.current = time;
            setMaxWatched(time);
        }
        saveProgress(time, total);
    };

    const onEnded = () => {
        const video = videoRef.current;
        if (!video) return;
        setIsPlaying(false);
        const total = video.duration || duration;
        maxWatchedRef.current = total;
        setMaxWatched(total);
        void saveProgress(total, total, true, false);
        clearLocalProgress(item.id);
    };

    const togglePlay = async () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            try {
                await video.play();
                setIsPlaying(true);
            } catch (err: any) {
                console.error('[TrainingVideoPlayer] play() failed', err);
                setLoadError(err?.message || 'Could not start playback.');
            }
        } else {
            video.pause();
            setIsPlaying(false);
            void saveProgress(video.currentTime, video.duration || duration, false, true, true);
        }
    };

    const handleVideoError = () => {
        const video = videoRef.current;
        const code = video?.error?.code;
        const codeName = code === 1 ? 'aborted'
            : code === 2 ? 'network'
            : code === 3 ? 'decode'
            : code === 4 ? 'source not supported / 403 / 404'
            : 'unknown';
        const msg = `Video failed to load (${codeName}). Source: ${resolvedSrc || '(empty)'}`;
        console.error('[TrainingVideoPlayer]', msg, video?.error);
        setLoadError(msg);
    };

    const restart = () => {
        const video = videoRef.current;
        if (!video) return;
        video.currentTime = 0;
        void video.play();
        setIsPlaying(true);
    };

    const toggleFullscreen = async () => {
        if (!playerRef.current) return;
        if (document.fullscreenElement) {
            await document.exitFullscreen();
        } else {
            await playerRef.current.requestFullscreen();
        }
    };

    const watchedPct = duration ? Math.min(100, Math.round((maxWatched / duration) * 100)) : 0;
    const currentPct = duration ? Math.min(100, Math.round((currentTime / duration) * 100)) : 0;

    const seekToClientX = (clientX: number, rect: DOMRect) => {
        const video = videoRef.current;
        if (!video || !duration) return;
        const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const cap = Math.max(0, maxWatchedRef.current);
        const target = Math.min(fraction * duration, cap);
        video.currentTime = target;
        setCurrentTime(target);
        saveProgress(target, duration);
    };

    const onScrubPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!duration) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        seekToClientX(event.clientX, event.currentTarget.getBoundingClientRect());
    };

    const onScrubPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!duration || (event.buttons & 1) === 0) return;
        seekToClientX(event.clientX, event.currentTarget.getBoundingClientRect());
    };

    return (
        <div
            ref={playerRef}
            className={`mb-1 space-y-4 rounded-md border p-4 ${isFullscreen ? 'flex h-screen flex-col justify-center border-black bg-black p-6 text-white sm:p-8' : ''}`}
        >
            <div className="group relative overflow-hidden rounded-md bg-black ring-1 ring-black/10">
                <video
                    ref={videoRef}
                    className="block aspect-video w-full bg-black"
                    preload="metadata"
                    src={resolvedSrc}
                    onClick={() => void togglePlay()}
                    onLoadedMetadata={onLoadedMetadata}
                    onSeeking={onSeeking}
                    onTimeUpdate={onTimeUpdate}
                    onEnded={onEnded}
                    onPause={() => {
                        setIsPlaying(false);
                        const v = videoRef.current;
                        if (v) void saveProgress(v.currentTime, v.duration || duration, false, true, true);
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onError={handleVideoError}
                    controls={false}
                    playsInline
                />
                {loadError && (
                    <div className="absolute inset-x-4 top-4 rounded-md bg-red-600/90 px-3 py-2 text-xs text-white">
                        {loadError}
                    </div>
                )}
                <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="absolute bottom-4 left-4 h-9 w-9 rounded-md bg-black/45 text-white backdrop-blur-sm hover:bg-black/60 hover:text-white"
                    onClick={() => void togglePlay()}
                >
                    {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-px" />}
                </Button>
                <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="absolute bottom-4 left-16 h-9 w-9 rounded-md bg-black/45 text-white backdrop-blur-sm hover:bg-black/60 hover:text-white"
                    onClick={restart}
                    title="Restart from beginning"
                >
                    <RotateCcw className="h-4 w-4" />
                </Button>
                <div className="absolute bottom-4 right-4 flex w-fit items-center gap-1.5 rounded-md bg-black/45 p-1 backdrop-blur-sm">
                    {isFullscreen &&
                        speeds.map(option => (
                            <button
                                key={option}
                                type="button"
                                className={`h-9 min-w-9 rounded-md px-2 text-xs font-medium transition-colors ${speed === option ? 'bg-white text-black' : 'text-white hover:bg-white/15'}`}
                                onClick={() => setSpeed(option)}
                            >
                                {option}x
                            </button>
                        ))}
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-9 w-9 rounded-md text-white hover:bg-white/15 hover:text-white"
                        onClick={() => void toggleFullscreen()}
                    >
                        {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                    </Button>
                </div>
            </div>
            <div className="space-y-1">
                <div
                    className={`group relative h-2 w-full ${duration ? 'cursor-pointer' : 'cursor-not-allowed'} touch-none rounded-full ${isFullscreen ? 'bg-white/20' : 'bg-gray-200'}`}
                    onPointerDown={onScrubPointerDown}
                    onPointerMove={onScrubPointerMove}
                    role="slider"
                    aria-valuemin={0}
                    aria-valuemax={duration || 0}
                    aria-valuenow={currentTime}
                    aria-label="Video position"
                >
                    {/* Unlocked (watched) range */}
                    <div
                        className={`absolute inset-y-0 left-0 rounded-full ${isFullscreen ? 'bg-white/40' : 'bg-gray-300'}`}
                        style={{ width: `${watchedPct}%`, transition: 'width 250ms linear' }}
                    />
                    {/* Current playback position */}
                    <div
                        className={`absolute inset-y-0 left-0 rounded-full ${isFullscreen ? 'bg-white' : 'bg-black'}`}
                        style={{ width: `${currentPct}%`, transition: 'width 250ms linear' }}
                    />
                    {/* Handle */}
                    {duration > 0 && (
                        <div
                            className={`pointer-events-none absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full shadow ${isFullscreen ? 'bg-white' : 'bg-black'}`}
                            style={{ left: `${currentPct}%`, transition: 'left 250ms linear' }}
                        />
                    )}
                </div>
                <div className={`flex items-center justify-between text-xs ${isFullscreen ? 'text-gray-200' : 'text-gray-500'}`}>
                    <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatSeconds(currentTime)} / {formatSeconds(duration)}
                    </span>
                    <span>{watchedPct}% watched</span>
                </div>
            </div>
            <div className={`text-xs ${isFullscreen ? 'text-gray-300' : 'text-gray-500'}`}>
                Click or drag the bar to jump to any point you've already watched. Skipping ahead is locked until you reach it.
            </div>
        </div>
    );
};
